// Tier-1 content batch — generic add/remove list editor for short string
// values (passports, languages). Same controlled-component contract as
// FilmsForm: the parent owns the rows and persists them via the page's
// single "Save changes" submit — NO save button here.
//
// Replace semantics live on the server: PUT /api/players/:id deletes the
// player's existing rows and inserts whatever the parent sends. Caps are
// server-enforced; the UI disables "Add" at the cap to match. Empty rows
// are dropped by the parent at submit time (mirrors the films payload
// filter), so a stray "Add" click never blocks a save.

import { Trash2, Plus, type LucideIcon } from 'lucide-react'

interface Props {
  title: string
  description: string
  icon: LucideIcon
  addLabel: string
  placeholder: string
  max: number
  rows: string[]
  onChange: (rows: string[]) => void
  // OPTIONAL (2026-07-30). When supplied, each row renders a <select> over
  // these values instead of a free-text input. When ABSENT the component
  // behaves exactly as before — PlayerEdit passes nothing and is unaffected.
  // A row whose current value is not in `options` gets that value prepended
  // to its own select, so stored data predating the list still renders rather
  // than silently blanking.
  options?: string[]
}

export default function StringListForm({
  title,
  description,
  icon: Icon,
  addLabel,
  placeholder,
  max,
  rows,
  onChange,
  options,
}: Props) {
  function updateRow(idx: number, value: string) {
    onChange(rows.map((r, i) => (i === idx ? value : r)))
  }

  function addRow() {
    if (rows.length >= max) return
    onChange([...rows, ''])
  }

  function removeRow(idx: number) {
    onChange(rows.filter((_, i) => i !== idx))
  }

  return (
    <section className="rounded-lg border border-hoop-border bg-hoop-bg p-5">
      <header className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-hoop-text-2" />
        <h2 className="text-sm font-semibold text-hoop-text">
          {title}
        </h2>
      </header>
      <p className="text-xs text-hoop-text-3 mb-4">
        {description} Up to {max}. Saved with{' '}
        <span className="font-medium">Save changes</span> below.
      </p>

      {rows.length === 0 ? (
        <p className="text-xs text-hoop-text-3 mb-3 italic">
          None yet.
        </p>
      ) : (
        <ul className="space-y-2 mb-3">
          {rows.map((r, idx) => (
            <li key={idx} className="flex items-center gap-2">
              {options ? (
                <select
                  value={r}
                  onChange={(e) => updateRow(idx, e.target.value)}
                  aria-label={`${title} ${idx + 1}`}
                  className="flex-1 min-w-0 rounded-md border border-hoop-border bg-hoop-surface px-2.5 py-1.5 text-sm text-hoop-text focus:outline-none focus:ring-2 focus:ring-hoop-accent"
                >
                  <option value="">{placeholder}</option>
                  {/* Unknown stored value keeps its own entry — see Props. */}
                  {r && !options.includes(r) && <option value={r}>{r}</option>}
                  {options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  autoComplete="off"
                  value={r}
                  onChange={(e) => updateRow(idx, e.target.value)}
                  placeholder={placeholder}
                  className="flex-1 min-w-0 rounded-md border border-hoop-border bg-hoop-surface px-2.5 py-1.5 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent"
                />
              )}
              <button
                type="button"
                onClick={() => removeRow(idx)}
                aria-label={`Remove ${title.toLowerCase()} ${idx + 1}`}
                className="shrink-0 text-hoop-text-3 hover:text-hoop-red p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={addRow}
        disabled={rows.length >= max}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-hoop-text-2 hover:text-hoop-text disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="w-3.5 h-3.5" />
        {addLabel}
        {rows.length >= max && (
          <span className="font-normal text-hoop-text-3">
            (max {max})
          </span>
        )}
      </button>
    </section>
  )
}
